<template>
  <header class="header">
    <div class="container">
      <div class="header-wrapper">
        <div class="logo-container">
          <router-link to="/" class="logo">
            <img src="@/assets/images/logo.svg" alt="白鲸 Logo" class="logo-img">
            <span class="logo-text">白鲸</span>
          </router-link>
        </div>
        
        <nav class="nav-menu">
          <router-link to="/" class="nav-item">{{ $t('nav.home') }}</router-link>
          <router-link to="/receive-code" class="nav-item">{{ $t('nav.receiveData') }}</router-link>
          <router-link to="/mail-receive" class="nav-item">{{ $t('nav.mailReceive') }}</router-link>
          <router-link to="/referral-rewards" class="nav-item">{{ $t('nav.referralRewards') }}</router-link>
          <div class="nav-more">
            <button
              type="button"
              class="nav-more-trigger"
              aria-haspopup="true"
            >
              <span>{{ $t('nav.more') }}</span>
              <svg class="nav-more-icon" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div class="nav-more-menu">
              <router-link to="/receive-records" class="nav-more-item">{{ $t('nav.receiveRecords') }}</router-link>
              <router-link to="/faq" class="nav-more-item">{{ $t('nav.faq') }}</router-link>
              <router-link to="/docs" class="nav-more-item">{{ $t('nav.apiDocs') }}</router-link>
              <router-link to="/desktop-download" class="nav-more-item">{{ $t('nav.desktopDownload') }}</router-link>
              <router-link to="/tickets" class="nav-more-item nav-more-ticket">
                {{ $t('nav.ticketCenter') }}
                <span v-if="supportUnreadCount > 0" class="ticket-notification-badge nav-more-ticket-badge">
                  {{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}
                </span>
              </router-link>
            </div>
          </div>
        </nav>
        
        <div class="header-right">
          <LanguageSwitcher class="language-switcher" />
          
          <div class="auth-actions" v-if="!isLoggedIn">
            <router-link to="/login" class="btn btn-outline login-btn">
              <span>{{ $t('nav.login') }}</span>
            </router-link>
          </div>
          
          <div class="user-container" v-if="isLoggedIn">
            <div class="user-dropdown" :class="{ active: userDropdownOpen }">
              <div class="user-info" @click="toggleUserDropdown">
                <img :src="userAvatar" alt="用户头像" class="user-avatar">
                <span class="user-name">{{ userName }}</span>
                <svg class="dropdown-icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="dropdown-menu">
                <router-link to="/user-center" class="dropdown-item" @click="closeUserDropdown">{{ $t('nav.userCenter') }}</router-link>
                <button @click="handleLogout" class="dropdown-item">{{ $t('nav.logout') }}</button>
              </div>
            </div>
          </div>
        </div>
        
        <button class="mobile-menu-btn" @click="toggleMobileMenu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3 6H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      
      <div class="mobile-menu" :class="{ active: mobileMenuOpen }">
        <nav class="mobile-nav">
          <router-link to="/" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.home') }}</router-link>
          <router-link to="/receive-code" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.receiveData') }}</router-link>
          <router-link to="/mail-receive" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.mailReceive') }}</router-link>
          <router-link to="/receive-records" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.receiveRecords') }}</router-link>
          <router-link to="/faq" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.faq') }}</router-link>
          <router-link to="/referral-rewards" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.referralRewards') }}</router-link>
          <router-link to="/docs" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.apiDocs') }}</router-link>
          <router-link to="/desktop-download" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.desktopDownload') }}</router-link>
          <router-link to="/tickets" class="mobile-nav-item message-item" @click="closeMobileMenu">
            {{ $t('nav.ticketCenter') }}
            <span v-if="supportUnreadCount > 0" class="mobile-ticket-notification-badge">
              {{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}
            </span>
          </router-link>
          <div v-if="!isLoggedIn">
            <router-link to="/login" class="btn btn-primary mobile-login-btn" @click="closeMobileMenu">{{ $t('nav.login') }}</router-link>
          </div>
          <div v-else class="mobile-user-actions">
            <router-link to="/user-center" class="mobile-nav-item" @click="closeMobileMenu">{{ $t('nav.userCenter') }}</router-link>
            <button @click="handleLogoutMobile" class="mobile-nav-item logout-btn">{{ $t('nav.logout') }}</button>
          </div>
        </nav>
      </div>
    </div>
  </header>
</template>

<script>
import { computed, ref, inject, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import defaultAvatar from '@/assets/images/default-avatar.svg'
import { isAuthenticated, getCurrentUser, logout } from '@/api/user'
import { getUserMessages } from '@/api/sms'
import { getTicketUnreadCount } from '@/api/ticket'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'

export default {
  name: 'HeaderComponent',
  components: {
    LanguageSwitcher
  },
  setup() {
    const router = useRouter()
    const { t: $t } = useI18n()
    const mobileMenuOpen = ref(false)
    const userDropdownOpen = ref(false)
    
    // 获取未读消息数量
    const unreadMessagesCount = ref(0)
    const unreadTicketsCount = ref(0)
    const supportUnreadCount = computed(() => unreadMessagesCount.value + unreadTicketsCount.value)
    let refreshTimer = null
    
    // 使用全局登录状态，如果不存在则回退到本地状态
    const globalIsLoggedIn = inject('globalIsLoggedIn', null)
    const updateGlobalLoginStatus = inject('updateGlobalLoginStatus', null)
    
    // 用户登录状态 - 使用注入的全局状态或本地状态
    const isLoggedIn = globalIsLoggedIn || ref(isAuthenticated())
    
    // 更新登录状态的函数
    const updateLoginStatus = () => {
      if (updateGlobalLoginStatus) {
        updateGlobalLoginStatus()
      } else {
        isLoggedIn.value = isAuthenticated()
      }
    }
    
    // 获取用户信息
    const userName = computed(() => {
      const user = getCurrentUser()
      return user ? user.name : '用户'
    })
    
    const userAvatar = computed(() => {
      const user = getCurrentUser()
      return user && user.avatar ? user.avatar : defaultAvatar
    })
    
    // 获取未读消息数量
    const fetchUnreadMessagesCount = async () => {
      if (!isAuthenticated()) return
      
      try {
        const res = await getUserMessages({
          page: 1,
          pageSize: 1,
          unread: true
        })
        
        if (res.success) {
          unreadMessagesCount.value = res.data.total || 0
        }
      } catch (error) {
        console.error($t('console.fetchUnreadCountFailed'), error)
      }
    }

    const fetchUnreadTicketsCount = async () => {
      if (!isAuthenticated()) {
        unreadTicketsCount.value = 0
        return
      }

      try {
        const res = await getTicketUnreadCount()
        const value = res.data && typeof res.data === 'object' ? res.data.count : res.data
        unreadTicketsCount.value = Number(value) || 0
      } catch (error) {
        // 工单接口不可用时不阻断全局导航，也不重复弹出错误提示。
        unreadTicketsCount.value = 0
        console.error('Fetch unread ticket count failed:', error)
      }
    }
    
    onMounted(() => {
      updateLoginStatus()
      fetchUnreadMessagesCount()
      fetchUnreadTicketsCount()
      refreshTimer = window.setInterval(() => {
        updateLoginStatus()
        fetchUnreadMessagesCount()
        fetchUnreadTicketsCount()
      }, 60000)
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
      window.addEventListener('support-unread-refresh', refreshSupportUnreadCount)
    })
    
    onUnmounted(() => {
      if (refreshTimer) window.clearInterval(refreshTimer)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('support-unread-refresh', refreshSupportUnreadCount)
    })
    
    // 监听路由变化，每次路由变化时更新登录状态
    watch(() => router.currentRoute.value.path, () => {
      updateLoginStatus()
      refreshSupportUnreadCount()
    })

    const refreshSupportUnreadCount = () => {
      fetchUnreadMessagesCount()
      fetchUnreadTicketsCount()
    }
    
    // 退出登录
    const handleLogout = async () => {
      closeUserDropdown() // 关闭下拉菜单
      await logout()
      unreadMessagesCount.value = 0
      unreadTicketsCount.value = 0
      ElMessage.success($t('messages.loggedOut'))
      updateLoginStatus() // 立即更新登录状态
      router.push('/')
    }
    
    const handleLogoutMobile = async () => {
      await handleLogout()
      closeMobileMenu()
    }
    
    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value
      // 打开移动菜单时关闭用户下拉菜单
      if (mobileMenuOpen.value) {
        closeUserDropdown()
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
    
    const closeMobileMenu = () => {
      mobileMenuOpen.value = false
      document.body.style.overflow = ''
    }
    
    // 用户下拉菜单控制
    const toggleUserDropdown = () => {
      userDropdownOpen.value = !userDropdownOpen.value
    }
    
    const closeUserDropdown = () => {
      userDropdownOpen.value = false
    }
    
    // 点击外部关闭下拉菜单
    const handleClickOutside = (event) => {
      const userDropdown = event.target.closest('.user-dropdown')
      if (!userDropdown && userDropdownOpen.value) {
        closeUserDropdown()
      }
    }
    
    // 键盘事件处理
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && userDropdownOpen.value) {
        closeUserDropdown()
      }
    }
    
    return {
      isLoggedIn,
      userName,
      userAvatar,
      mobileMenuOpen,
      userDropdownOpen,
      unreadMessagesCount,
      unreadTicketsCount,
      supportUnreadCount,
      handleLogout,
      handleLogoutMobile,
      toggleMobileMenu,
      closeMobileMenu,
      toggleUserDropdown,
      closeUserDropdown
    }
  }
}
</script>

<style scoped>
.header {
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-color);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  height: var(--header-height);
  transition: top 0.3s ease;
}

/* 当有中国横幅时，调整头部位置 */
body:has(.china-banner) .header {
  top: 40px;
}

@media (max-width: 480px) {
  body:has(.china-banner) .header {
    top: 36px;
  }
}

.header-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--header-height);
}

.logo-container {
  display: flex;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-img {
  height: 32px;
  margin-right: 8px;
}

.logo-text {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
}

.nav-menu {
  display: flex;
  gap: 20px;
}

.nav-item {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  text-decoration: none;
  transition: color 0.2s;
  position: relative;
  padding: 8px 0;
}

.nav-item:hover, .nav-item.router-link-active {
  color: var(--primary-color);
}

.nav-item.router-link-active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
}

.nav-more {
  position: relative;
  display: flex;
  align-items: center;
}

.nav-more-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
  border: none;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  transition: color 0.2s;
}

.nav-more-icon {
  transition: transform 0.2s;
}

.nav-more-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  min-width: 128px;
  padding: 8px 0;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background-color: var(--card-background);
  box-shadow: var(--shadow-md);
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, 10px);
  transition: opacity 0.2s, transform 0.2s, visibility 0.2s;
  z-index: 10;
}

.nav-more:hover .nav-more-menu,
.nav-more:focus-within .nav-more-menu {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}

.nav-more:hover .nav-more-trigger,
.nav-more:focus-within .nav-more-trigger,
.nav-more:has(.nav-more-item.router-link-active) .nav-more-trigger {
  color: var(--primary-color);
}

.nav-more:hover .nav-more-icon,
.nav-more:focus-within .nav-more-icon {
  transform: rotate(180deg);
}

.nav-more-item {
  display: block;
  padding: 10px 16px;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.2s, color 0.2s;
}

.nav-more-item:hover,
.nav-more-item.router-link-active {
  background-color: var(--hover-color);
  color: var(--primary-color);
}

.nav-more-ticket {
  position: relative;
}

.nav-more-ticket-badge {
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
}

.ticket-notification-badge,
.mobile-ticket-notification-badge {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

.ticket-notification-badge {
  position: absolute;
  top: -2px;
  right: -18px;
  padding: 0 5px;
}

.mobile-ticket-notification-badge {
  position: absolute;
  top: 50%;
  right: 20px;
  padding: 0 5px;
  transform: translateY(-50%);
}

.message-item {
  position: relative;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.language-switcher {
  margin-right: 8px;
}

.auth-actions {
  display: flex;
  align-items: center;
}

.login-btn {
  padding: 10px 20px;
  font-weight: 600;
}

.user-container {
  position: relative;
}

.user-dropdown {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.user-info:hover,
.user-dropdown.active .user-info {
  background-color: var(--hover-color);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 8px;
  object-fit: cover;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-right: 4px;
}

.dropdown-icon {
  color: var(--text-tertiary);
  transition: transform 0.2s;
}

.user-dropdown:hover .dropdown-icon,
.user-dropdown.active .dropdown-icon {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  width: 180px;
  background-color: var(--card-background);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 8px 0;
  margin-top: 8px;
  border: 1px solid var(--border-color);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.2s;
  z-index: 10;
}

.user-dropdown:hover .dropdown-menu,
.user-dropdown.active .dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: block;
  padding: 10px 16px;
  font-size: 14px;
  color: var(--text-primary);
  text-decoration: none;
  transition: background-color 0.2s;
  cursor: pointer;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font-family: inherit;
}

.dropdown-item:hover {
  background-color: var(--hover-color);
  color: var(--primary-color);
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  padding: 8px;
}

.mobile-menu {
  display: none;
  position: fixed;
  top: var(--header-height);
  left: 0;
  width: 100%;
  height: calc(100vh - var(--header-height));
  background-color: var(--card-background);
  z-index: 999;
  padding: 24px;
  transform: translateX(100%);
  transition: transform 0.3s;
  overflow-y: auto;
}

.mobile-menu.active {
  transform: translateX(0);
}

.mobile-nav {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-nav-item {
  font-size: 18px;
  font-weight: 500;
  color: var(--text-primary);
  text-decoration: none;
  padding: 12px 0 12px 16px;
  border-bottom: 1px solid var(--border-color);
  position: relative;
  transition: color 0.2s;
}

.mobile-nav-item:hover,
.mobile-nav-item.router-link-active {
  color: var(--primary-color);
}

.mobile-nav-item.router-link-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--primary-color);
}

.mobile-login-btn {
  margin-top: 16px;
  width: 100%;
}

.mobile-user-actions {
  margin-top: 16px;
}

.logout-btn {
  width: 100%;
  background: none;
  border: none;
  font-family: inherit;
  font-size: 18px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  padding: 12px 0;
  color: #FF6B6B;
  border-bottom: 1px solid var(--border-color);
}

@media (max-width: 768px) {
  .nav-menu, .auth-actions, .user-container {
    display: none;
  }
  
  .mobile-menu-btn {
    display: block;
  }
  
  .mobile-menu {
    display: block;
  }
}
</style>
